// eslint-disable-next-line no-secrets/no-secrets
/**
 * Shared form-state hook for the Campaign form (Create, Modify, View).
 *
 * Thin orchestrator that composes 6 focused sub-hooks:
 * - useCommonFormState: title, merchant, funding, asins, dates
 * - useNcemiFormState: shares, brand, cards, tenures, price leading
 * - useCashbackFormState: prime, T&C, guardrails, rounding
 * - useBuybackFormState: exclusions, sweetener, guardrails
 * - useDealFormState: deal percent guardrail
 * - useInstantBankDiscountFormState: bank names, instruments, event type, T&C, budget, guardrails
 *
 * Returns the unified UseCampaignFormResult — consumers see no API change.
 */

import { CampaignType, UserRole } from 'src/@types/valueMatching/enums';
import { CampaignConfigurationData } from 'src/@types/valueMatching/campaignConfiguration';
import { FormMode } from 'src/components/organisms/CampaignForm/types';
import { CampaignData } from 'src/@types/valueMatching/campaign';
import { useCommonFormState } from './useCommonFormState';
import { useNcemiFormState } from './useNcemiFormState';
import { useCashbackFormState } from './useCashbackFormState';
import { useBuybackFormState } from './useBuybackFormState';
import { useDealFormState } from './useDealFormState';
import { useInstantBankDiscountFormState } from './useInstantBankDiscountFormState';

// Re-export helpers so consumers can use them directly
export { getDefaultMerchantGroup, getCpgnAccountsForMerchant } from './helpers';

// ============================================================================
// Hook params & result types
// ============================================================================

export interface UseCampaignFormParams {
    /**
     * Campaign configuration fetched from the API; null while loading or on error.
     * Provides merchant groups, funding accounts, card configs, and the user role.
     */
    config: CampaignConfigurationData | null;
    /**
     * The resolved campaign type (e.g. NCEMI, CASHBACK).
     * `undefined` while the slug is being resolved — sub-hooks skip initialisation until it is defined.
     */
    campaignType: CampaignType | undefined;
    /** Whether the merchant group selector is visible (true for NCEMI/DEAL campaigns). */
    showMerchantGroup: boolean;
    /** Current form mode — determines field editability and initialisation behaviour. */
    mode: FormMode;
    /**
     * Pre-existing campaign data used to populate fields in modify/view mode.
     * In create mode this is ignored. `config` provides the *structure* (options, defaults)
     * while `initialData` provides the *saved values* of an existing campaign.
     */
    initialData?: CampaignData;
}

export function useCampaignForm({
    config,
    campaignType,
    showMerchantGroup,
    mode,
    initialData,
}: UseCampaignFormParams) {
    // ── Compose sub-hooks ───────────────────────────────────────────────
    const common = useCommonFormState({ config, campaignType, showMerchantGroup, mode, initialData });
    const ncemi = useNcemiFormState({ config, campaignType, mode, initialData });
    const cashback = useCashbackFormState({ mode, initialData });
    const buyback = useBuybackFormState({ mode, initialData });
    const deal = useDealFormState({ mode, initialData });
    const instantBankDiscount = useInstantBankDiscountFormState({ mode, initialData, config, campaignType });

    // ── Derived values ──────────────────────────────────────────────────
    const isNcemi = campaignType === CampaignType.NO_COST_EMI;
    const isCashback = campaignType === CampaignType.CASHBACK;
    const isBuyback = campaignType === CampaignType.BUYBACK;
    const isDeal = campaignType === CampaignType.DEAL;
    const isInstantBankDiscount = campaignType === CampaignType.INSTANT_BANK_DISCOUNT;
    const role = config?.role ?? UserRole.BRAND;
    const isAmazonRole = role === UserRole.AMAZON;

    return {
        // Common form state
        ...common,

        // NCEMI form state
        ...ncemi,

        // CASHBACK form state
        ...cashback,

        // BUYBACK form state
        ...buyback,

        // DEAL form state
        ...deal,

        // INSTANT BANK DISCOUNT form state
        ...instantBankDiscount,

        // Derived / computed values
        isNcemi,
        isCashback,
        isBuyback,
        isDeal,
        isInstantBankDiscount,
        role,
        isAmazonRole,
    };
}

/** Type alias for the hook result. */
export type UseCampaignFormResult = ReturnType<typeof useCampaignForm>;
